import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, BookCheck, Settings2, History } from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import api, { getStoredUser } from '../api';
import { useToast } from '../hooks/use-toast';
import { BookkeepingGrid, monthLabel } from '../components/accounting/BookkeepingGrid';
import { ManageClientsDialog } from '../components/accounting/ManageClientsDialog';

const errMsg = (e) => e?.response?.data?.detail || 'Something went wrong';

const HistoryList = ({ items }) => (
  <div className="rounded-xl bg-white border border-stone-200 divide-y divide-stone-100" data-testid="bk-history">
    {items.length === 0 && <div className="p-6 text-sm text-stone-500 text-center">No approved months yet.</div>}
    {items.map((h) => (
      <div key={h.id} className="px-4 py-3 flex flex-wrap justify-between gap-2 text-sm" data-testid={`bk-history-${h.id}`}>
        <span className="font-medium text-stone-900">{h.clientName} · {monthLabel(h.month)}</span>
        <span className="text-stone-500">Prepared by {h.submittedBy} · approved by {h.approvedBy} on {new Date(h.approvedAt).toLocaleDateString()}</span>
      </div>
    ))}
  </div>
);

export default function Accounting() {
  const nav = useNavigate();
  const { toast } = useToast();
  const user = getStoredUser();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [manage, setManage] = useState(false);
  const [history, setHistory] = useState(null);

  const load = useCallback(() => api.get('/bookkeeping').then((r) => setData(r.data)), []);
  useEffect(() => { if (!localStorage.getItem('ad_token')) { nav('/login'); return; } load(); }, [load, nav]);

  const patchRow = (row) => setData((d) => ({ ...d, rows: d.rows.map((r) => (r.clientId === row.clientId ? row : r)) }));
  const onTick = async (row, key, state) => {
    patchRow({ ...row, items: { ...row.items, [key]: state ? { state } : undefined } });
    try { patchRow((await api.post(`/bookkeeping/${row.clientId}/tick`, { key, state })).data); } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); load(); }
  };
  const onAction = async (row, kind) => {
    const note = kind === 'reject' ? window.prompt('What needs fixing? (optional)') : null;
    if (kind === 'reject' && note === null) return;
    setBusyId(row.clientId);
    try {
      const r = (await api.post(`/bookkeeping/${row.clientId}/${kind}`, kind === 'reject' ? { note } : undefined)).data;
      patchRow(r);
      toast({ title: kind === 'approve' ? `${monthLabel(row.month)} approved — ${monthLabel(r.month)} is ready` : kind === 'submit' ? 'Sent for partner approval' : 'Sent back to the team' });
      if (history) setHistory(null);
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); } finally { setBusyId(null); }
  };
  const onToggle = async (cid, enabled) => {
    try { await api.put(`/bookkeeping/clients/${cid}`, { enabled }); await load(); } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };
  const showHistory = async () => setHistory(history ? null : (await api.get('/bookkeeping/history')).data);

  const pending = data?.rows.filter((r) => r.status === 'submitted').length || 0;
  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Accounting"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2"><BookCheck size={24} className="text-emerald-800"/> Accounting</h1>
              <p className="mt-1 text-stone-600 text-sm">Monthly bookkeeping close up to trial balance. Tick each item off, then send the client's month for partner approval.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={showHistory} data-testid="bk-history-btn"><History size={15} className="mr-1.5"/>{history ? 'Hide approved' : 'Approved months'}</Button>
              <Button className="bg-emerald-800 hover:bg-emerald-900" onClick={() => setManage(true)} data-testid="bk-manage-btn"><Settings2 size={15} className="mr-1.5"/>Choose clients</Button>
            </div>
          </div>
          {!data ? <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-stone-400"/></div> : (
            <>
              <div className="flex flex-wrap gap-4 text-xs text-stone-600" data-testid="bk-summary">
                <span><b className="text-stone-900">{data.rows.length}</b> clients on bookkeeping</span>
                <span><b className="text-stone-900">{data.rows.filter((r) => r.complete && r.status === 'open').length}</b> ready to send</span>
                <span><b className="text-violet-700">{pending}</b> awaiting partner approval</span>
                <span className="text-stone-400">Click a box: done → N/A → clear. Greyed N/A boxes don't apply to that client.</span>
              </div>
              {history && <HistoryList items={history}/>}
              {data.rows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-stone-300 bg-white p-10 text-center" data-testid="bk-empty">
                  <p className="text-stone-600 text-sm">No clients on bookkeeping yet.</p>
                  <Button className="mt-4 bg-emerald-800 hover:bg-emerald-900" onClick={() => setManage(true)} data-testid="bk-empty-choose-btn">Choose clients</Button>
                </div>
              ) : <BookkeepingGrid columns={data.columns} rows={data.rows} isOwner={data.isOwner} busyId={busyId} onTick={onTick} onAction={onAction}/>}
              <ManageClientsDialog open={manage} onOpenChange={setManage} rows={data.rows} available={data.available} onToggle={onToggle}/>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
